import { MIN_VENUES_FOR_RATES, VENUE_SAMPLES_SHOWN } from "./constants";
import type { VenueRateRow } from "./db-types";
import type { HotelRate } from "./types";
import { cityKey } from "./workspace";

/** Prefix of the client cache keys for hotel rates. */
export const HOTEL_RATE_KEY_PREFIX = "hotel:";

/**
 * The client cache key for a city's hotel rate, which depends on its pinned airport.
 * @param city - City name.
 * @param code - Pinned airport code, if any.
 * @returns The key, e.g. `hotel:lisbon|LIS`.
 * @throws Never.
 */
export const hotelRateKey = (city: string, code: string | null): string =>
  `${HOTEL_RATE_KEY_PREFIX}${cityKey(city)}|${code ?? ""}`;

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/**
 * Derives a city's typical nightly hotel range from venue rate rows.
 *
 * Several countries share city names (London, England and London, Ontario), so only the
 * country with the most venues counts. The range runs from the median low rate to the
 * median high rate, which keeps one luxury outlier from setting the band.
 *
 * @param rows - Venue rows already matched to the city by name.
 * @returns Venue-backed rates, or `none` when too few venues carry a rate.
 * @throws Never.
 */
export const summarizeVenueRates = (rows: VenueRateRow[]): HotelRate => {
  const rated = rows.filter(
    (row): row is VenueRateRow & { rate_lower_usd: number; rate_upper_usd: number } =>
      (row.rate_lower_usd ?? 0) > 0 && (row.rate_upper_usd ?? 0) >= (row.rate_lower_usd ?? 0)
  );
  const byCountry = new Map<string, typeof rated>();
  for (const row of rated) {
    const country = (row.country ?? "").trim().toLowerCase();
    byCountry.set(country, [...(byCountry.get(country) ?? []), row]);
  }
  const largest = [...byCountry.values()].sort((a, b) => b.length - a.length)[0] ?? [];
  if (largest.length < MIN_VENUES_FOR_RATES) return { status: "none" };
  const low = Math.round(median(largest.map((row) => row.rate_lower_usd)));
  const high = Math.round(median(largest.map((row) => row.rate_upper_usd)));
  const samples = largest
    .filter((row) => row.venue_name)
    .sort((a, b) => Math.abs(a.rate_lower_usd - low) - Math.abs(b.rate_lower_usd - low))
    .slice(0, VENUE_SAMPLES_SHOWN)
    .map((row) => ({ name: row.venue_name!, low: row.rate_lower_usd, high: row.rate_upper_usd }));
  const updated = largest.map((row) => row.updated_at).filter((at): at is string => Boolean(at));
  return {
    status: "venues",
    perNight: { low, high },
    venueCount: largest.length,
    samples,
    updatedAt: updated.length ? updated.reduce((latest, at) => (at > latest ? at : latest)) : null,
  };
};
